import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import CallRoundedIcon from '@mui/icons-material/CallRounded'
import DialpadRoundedIcon from '@mui/icons-material/DialpadRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SettingsVoiceRoundedIcon from '@mui/icons-material/SettingsVoiceRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  endPortalVoiceCall,
  extendPortalVoiceCall,
  getPortalVoiceFriendlyErrorMessage,
  refreshPortalVoiceAudioDevices,
  sendPortalVoiceDtmf,
  setPortalVoiceCallVolumeBoostPercent,
  setPortalVoiceInputDevice,
  setPortalVoiceOutputDevice,
  startPortalVoiceCall,
  togglePortalVoiceHold,
  togglePortalVoiceMute,
} from 'src/mentor/components/voice/portalVoiceCallManager'

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']

function formatElapsed(seconds) {
  const safe = Math.max(0, Number(seconds) || 0)
  const minutes = Math.floor(safe / 60)
  const secs = safe % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

function formatRemaining(seconds) {
  const safe = Math.max(0, Number(seconds) || 0)
  const minutes = Math.floor(safe / 60)
  const secs = safe % 60
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

const STATUS_META = {
  connecting: { label: 'Preparing call', color: '#2563eb' },
  ringing: { label: 'Ringing', color: '#d97706' },
  connected: { label: 'Live', color: '#059669' },
  reconnecting: { label: 'Reconnecting', color: '#d97706' },
  'on-hold': { label: 'On hold', color: '#7c3aed' },
  ending: { label: 'Ending', color: '#64748b' },
  ended: { label: 'Ended', color: '#64748b' },
}

// The call control cluster. Reads the passed-in global call state; drives the shared voice
// manager (mute/hold/DTMF/devices/extend/end) so the actual call is the real browser bridge.
export default function SupportCallControls({
  callState,
  contact,
  routes,
  routesLoading,
  routesError,
  onReloadRoutes,
}) {
  const status = callState?.status || 'idle'
  const isLive = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending'].includes(status)
  const [selectedDestinationType, setSelectedDestinationType] = useState('')
  const [starting, setStarting] = useState(false)
  const [audioPanelOpen, setAudioPanelOpen] = useState(false)
  const [dialpadOpen, setDialpadOpen] = useState(false)
  const [audioBusy, setAudioBusy] = useState('')

  const enabledRoutes = useMemo(() => (routes || []).filter((route) => route?.enabled), [routes])
  const defaultRoute = useMemo(() => {
    if (selectedDestinationType) {
      return (routes || []).find((route) => (route?.destinationType || route?.id) === selectedDestinationType)
    }
    return enabledRoutes[0] || (routes || [])[0] || null
  }, [routes, enabledRoutes, selectedDestinationType])

  // Keep a valid route selected: seed it, and reset if the selection isn't among the current
  // routes (e.g. the surface switched to a different contact in place).
  useEffect(() => {
    const exists = (routes || []).some((route) => (route?.destinationType || route?.id) === selectedDestinationType)
    if (selectedDestinationType && exists) {
      return
    }
    const fallback = enabledRoutes[0] || (routes || [])[0]
    const nextValue = fallback?.destinationType || fallback?.id || ''
    if (nextValue !== selectedDestinationType) {
      setSelectedDestinationType(nextValue)
    }
  }, [enabledRoutes, routes, selectedDestinationType])

  async function handleStartCall() {
    const route = defaultRoute
    if (!route || !route.enabled) {
      toast.error(route?.disabledReason || 'This call route is unavailable right now.')
      return
    }
    setStarting(true)
    try {
      await startPortalVoiceCall({
        actorRole: 'support-agent',
        bookedEventId: contact?.bookedEventId || undefined,
        clinicBrandId: contact?.clinicBrandId || undefined,
        clinicId: contact?.clinicId || undefined,
        practitionerId: contact?.practitionerId || undefined,
        patientId: contact?.patientId || undefined,
        targetRole: contact?.targetRole || 'patient',
        eventType: contact?.eventType || undefined,
        destinationType: route?.destinationType || route?.id,
        uiSurface: 'support-call-page',
        uiInstanceKey: 'support-call-surface',
        providerTypeHint: route?.providerType || null,
      })
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the call.'))
    } finally {
      setStarting(false)
    }
  }

  function handleMute() {
    try {
      togglePortalVoiceMute()
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to update mute.'))
    }
  }

  function handleHold() {
    try {
      togglePortalVoiceHold()
    } catch (error) {
      toast.error(error?.message || 'Unable to update hold.')
    }
  }

  async function handleExtend() {
    try {
      await extendPortalVoiceCall()
    } catch (error) {
      toast.error(error?.message || 'Unable to extend the call.')
    }
  }

  async function handleRefreshDevices() {
    setAudioBusy('refresh')
    try {
      await refreshPortalVoiceAudioDevices()
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to load audio devices.'))
    } finally {
      setAudioBusy('')
    }
  }

  async function handleInputDevice(event) {
    const deviceId = event?.target?.value
    if (!deviceId) {
      return
    }
    setAudioBusy('input')
    try {
      await setPortalVoiceInputDevice(deviceId)
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to switch microphone.'))
    } finally {
      setAudioBusy('')
    }
  }

  async function handleOutputDevice(event) {
    const deviceId = event?.target?.value || 'default'
    setAudioBusy('output')
    try {
      await setPortalVoiceOutputDevice(deviceId)
    } catch (error) {
      toast.error(error?.message || 'Unable to switch speaker.')
    } finally {
      setAudioBusy('')
    }
  }

  const statusMeta = STATUS_META[status] || null
  const canMute = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold'].includes(status)
  const canHold = ['connected', 'on-hold'].includes(status)
  const inputDevices = callState?.availableInputDevices || []
  const outputDevices = callState?.availableOutputDevices || []
  const volumeBoost = Math.max(100, Math.min(250, Number(callState?.volumeBoostPercent) || 100))

  if (!isLive) {
    return (
      <Stack spacing={1.25}>
        {routesLoading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>Loading call routes…</Typography>
          </Stack>
        ) : routesError ? (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12.5, color: '#b91c1c' }}>{routesError}</Typography>
            <Button size="small" onClick={onReloadRoutes} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Retry
            </Button>
          </Stack>
        ) : (routes || []).length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
            No call routes are configured for this contact.
          </Typography>
        ) : (
          <>
            {routes.length > 1 ? (
              <TextField
                select
                size="small"
                label="Call route"
                value={selectedDestinationType}
                onChange={(event) => setSelectedDestinationType(event.target.value)}
                sx={{ '& .MuiInputBase-root': { borderRadius: '10px', fontSize: 13 } }}
              >
                {routes.map((route) => {
                  const value = route?.destinationType || route?.id
                  return (
                    <MenuItem key={value} value={value} disabled={!route?.enabled}>
                      {route?.title || route?.destinationLabel || value}
                      {route?.enabled ? '' : ' (unavailable)'}
                    </MenuItem>
                  )
                })}
              </TextField>
            ) : null}
            {defaultRoute && !defaultRoute.enabled ? (
              <Typography sx={{ fontSize: 12, color: '#b45309' }}>
                {defaultRoute?.disabledReason || 'This route is currently unavailable.'}
              </Typography>
            ) : null}
            <Button
              variant="contained"
              startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <CallRoundedIcon />}
              onClick={handleStartCall}
              disabled={starting || !defaultRoute?.enabled}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: '12px',
                py: 1.1,
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0 12px 24px rgba(37, 99, 235, 0.24)',
                '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)' },
              }}
            >
              {starting ? 'Connecting…' : `Call ${contact?.name || 'contact'}`}
            </Button>
          </>
        )}
      </Stack>
    )
  }

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {statusMeta ? (
          <Chip
            size="small"
            label={statusMeta.label}
            sx={{ height: 22, fontWeight: 700, color: '#fff', backgroundColor: statusMeta.color }}
          />
        ) : null}
        {callState?.connectedAtMs ? (
          <Chip size="small" label={`Live ${formatElapsed(callState.elapsedSeconds)}`} sx={{ height: 22, fontWeight: 700 }} />
        ) : null}
        {callState?.remainingSeconds > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Ends in ${formatRemaining(callState.remainingSeconds)}`}
            sx={{ height: 22, fontWeight: 700 }}
          />
        ) : null}
        {callState?.muted ? <Chip size="small" color="warning" label="Muted" sx={{ height: 22, fontWeight: 700 }} /> : null}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant={callState?.muted ? 'contained' : 'outlined'}
          color={callState?.muted ? 'warning' : 'inherit'}
          startIcon={callState?.muted ? <MicOffRoundedIcon /> : <MicRoundedIcon />}
          onClick={handleMute}
          disabled={!canMute || callState?.holdActive}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
        >
          {callState?.muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button
          size="small"
          variant={callState?.holdActive ? 'contained' : 'outlined'}
          color={callState?.holdActive ? 'secondary' : 'inherit'}
          startIcon={callState?.holdActive ? <PlayCircleFilledRoundedIcon /> : <PauseCircleFilledRoundedIcon />}
          onClick={handleHold}
          disabled={!canHold}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
        >
          {callState?.holdActive ? 'Resume' : 'Hold'}
        </Button>
        <Button
          size="small"
          variant={dialpadOpen ? 'contained' : 'outlined'}
          color={dialpadOpen ? 'primary' : 'inherit'}
          startIcon={<DialpadRoundedIcon />}
          onClick={() => setDialpadOpen((value) => !value)}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
        >
          Keypad
        </Button>
        <Button
          size="small"
          variant={audioPanelOpen ? 'contained' : 'outlined'}
          color={audioPanelOpen ? 'primary' : 'inherit'}
          startIcon={<SettingsVoiceRoundedIcon />}
          onClick={() => {
            setAudioPanelOpen((value) => !value)
            if (!audioPanelOpen && !inputDevices.length && !outputDevices.length) {
              void handleRefreshDevices()
            }
          }}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
        >
          Audio
        </Button>
      </Stack>

      <Collapse in={dialpadOpen} unmountOnExit>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0.75,
            p: 1,
            borderRadius: '14px',
            border: '1px solid rgba(37, 99, 235, 0.14)',
            backgroundColor: 'rgba(37, 99, 235, 0.04)',
          }}
        >
          {DTMF_KEYS.map((key) => (
            <Button
              key={key}
              onClick={() => sendPortalVoiceDtmf(key)}
              sx={{
                minWidth: 0,
                py: 1,
                borderRadius: '10px',
                fontWeight: 800,
                fontSize: 18,
                color: '#1e293b',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                '&:hover': { backgroundColor: '#eef2ff' },
              }}
            >
              {key}
            </Button>
          ))}
        </Box>
      </Collapse>

      <Collapse in={audioPanelOpen} unmountOnExit>
        <Stack
          spacing={1.25}
          sx={{ p: 1.25, borderRadius: '14px', border: '1px solid rgba(37, 99, 235, 0.14)', backgroundColor: 'rgba(37, 99, 235, 0.04)' }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#334155' }}>Audio boost</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{volumeBoost}%</Typography>
              <IconButton size="small" onClick={handleRefreshDevices} disabled={audioBusy === 'refresh'}>
                <RefreshRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Stack>
          <Slider
            size="small"
            min={100}
            max={250}
            step={10}
            value={volumeBoost}
            onChange={(_, value) => setPortalVoiceCallVolumeBoostPercent(Array.isArray(value) ? value[0] : value)}
          />
          <TextField
            select
            size="small"
            label="Microphone"
            value={callState?.selectedInputDeviceId || ''}
            onChange={handleInputDevice}
            disabled={!inputDevices.length || audioBusy === 'input'}
            sx={{ '& .MuiInputBase-root': { borderRadius: '10px', fontSize: 13 } }}
          >
            {inputDevices.length ? (
              inputDevices.map((device) => (
                <MenuItem key={device?.deviceId || 'mic'} value={device?.deviceId || ''}>
                  {device?.label || 'Microphone'}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">No microphones found</MenuItem>
            )}
          </TextField>
          <TextField
            select
            size="small"
            label="Speaker"
            value={callState?.selectedOutputDeviceId || 'default'}
            onChange={handleOutputDevice}
            disabled={!outputDevices.length || audioBusy === 'output' || !callState?.outputDeviceSelectionSupported}
            sx={{ '& .MuiInputBase-root': { borderRadius: '10px', fontSize: 13 } }}
          >
            {outputDevices.length ? (
              outputDevices.map((device) => (
                <MenuItem key={device?.deviceId || 'spk'} value={device?.deviceId || 'default'}>
                  {device?.label || 'Speaker'}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="default">System default speaker</MenuItem>
            )}
          </TextField>
          {!callState?.outputDeviceSelectionSupported ? (
            <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
              Speaker switching depends on browser support for routed audio output.
            </Typography>
          ) : null}
        </Stack>
      </Collapse>

      {callState?.error ? (
        <Typography sx={{ fontSize: 12, color: '#b91c1c' }}>{callState.error}</Typography>
      ) : null}

      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        {callState?.renewalEnabled ? (
          <Button
            size="small"
            variant="outlined"
            onClick={handleExtend}
            disabled={!callState?.renewalAllowed || callState?.renewalInFlight}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
          >
            {callState?.renewalInFlight ? 'Extending…' : 'Extend'}
          </Button>
        ) : (
          <Box />
        )}
        <Button
          size="small"
          color="error"
          variant="contained"
          startIcon={<CallEndRoundedIcon />}
          onClick={() => endPortalVoiceCall()}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
        >
          End call
        </Button>
      </Stack>
    </Stack>
  )
}
